import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, CheckCircle, ArrowRight } from 'lucide-react';
import { AnimatedButton } from '../AnimatedButton';
import { springConfig, confettiConfig, type AuthTheme } from '../constants';

interface SuccessStepProps {
  onContinue: () => void;
  theme?: AuthTheme;
}

export function SuccessStep({ onContinue, theme = 'invol' }: SuccessStepProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Confetti animation
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const updateSize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    updateSize();

    // Create confetti particles
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      color: string;
      size: number;
      rotation: number;
      rotationSpeed: number;
      shape: 'square' | 'circle';
    }

    const particles: Particle[] = [];
    const { particleCount, spread, startVelocity, colors, shapes } = confettiConfig;

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.random() * spread - spread / 2) * (Math.PI / 180) - Math.PI / 2;
      particles.push({
        x: canvas.offsetWidth / 2,
        y: canvas.offsetHeight / 2,
        vx: Math.cos(angle) * startVelocity * (0.5 + Math.random() * 0.5),
        vy: Math.sin(angle) * startVelocity * (0.5 + Math.random() * 0.5),
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 6 + Math.random() * 6,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
      });
    }

    let animationId: number;
    const gravity = 0.3;
    const decay = 0.98;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      let activeParticles = 0;
      particles.forEach((p) => {
        p.vy += gravity;
        p.vx *= decay;
        p.vy *= decay;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        if (p.y < canvas.offsetHeight + 50) {
          activeParticles++;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = p.color;

          if (p.shape === 'circle') {
            ctx.beginPath();
            ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          }

          ctx.restore();
        }
      });

      if (activeParticles > 0) {
        animationId = requestAnimationFrame(animate);
      }
    };

    // Start animation after a short delay
    const timeout = setTimeout(() => {
      animate();
    }, 300);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="relative text-center py-4"
    >
      {/* Confetti canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 10 }}
      />

      {/* Success icon */}
      <motion.div
        className="w-20 h-20 rounded-3xl mx-auto mb-6 flex items-center justify-center shadow-xl bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30"
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ ...springConfig.celebration, delay: 0.1 }}
      >
        <PartyPopper className="h-10 w-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        className="text-2xl font-bold text-slate-900 mb-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        You're all set!
      </motion.h2>

      {/* Message */}
      <motion.p
        className="text-slate-500 mb-8 max-w-sm mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        Your account is ready! Taking you to your dashboard...
      </motion.p>

      {/* Status badge */}
      <motion.div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 bg-emerald-50 text-emerald-700"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, ...springConfig.bouncy }}
      >
        <CheckCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Account Active</span>
      </motion.div>

      {/* Decorative dots */}
      <div className="flex justify-center gap-2 mb-8">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-emerald-400"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.4 + i * 0.1,
              ...springConfig.bouncy,
            }}
          />
        ))}
      </div>

      {/* Action button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <AnimatedButton
          onClick={onContinue}
          variant="primary"
          size="lg"
          icon={<ArrowRight className="h-4 w-4" />}
          theme={theme}
        >
          Go to Dashboard
        </AnimatedButton>
      </motion.div>

      {/* Auto-redirect notice */}
      <motion.p
        className="text-xs text-slate-400 mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        Redirecting to your dashboard...
      </motion.p>
    </motion.div>
  );
}
